/**
 * pages/MyRecentlyViewed.jsx
 * --------------------------
 * Buyer route — full-page "Recently Viewed" list. Reads from the existing
 * Zustand `recentlyViewed` store (no new API). Wraps the unified
 * `<DashboardLayout>` shell so the buyer sidebar is visible.
 *
 * v2.71.33 — Added to close a sidebar/route mismatch (`/account/recent` was
 * advertised in the buyer sidebar but no route existed).
 */

import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ClipboardList, Building2, MapPin } from "lucide-react";

import { useAuth } from "../lib/AuthContext";
import { formatINR } from "../shared/services/api";
import { DashboardLayout } from "../features/dashboard";
import { useRecentlyViewedItems, useRecentlyViewedStore } from "../store";
import { Button } from "../components/ui/button";

export default function MyRecentlyViewed() {
  const { user, loading } = useAuth();
  const items = useRecentlyViewedItems();
  const clear = useRecentlyViewedStore((s) => s.clear);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/account/recent" replace />;

  return (
    <DashboardLayout title="Recently Viewed" subtitle="Properties you have browsed">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[12px] text-[#71717A]">
          {items.length} {items.length === 1 ? "property" : "properties"}
        </div>
        {items.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={clear}
            data-testid="recent-clear-btn"
          >
            Clear history
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#E4E4E7] rounded-sm p-10 text-center"
          data-testid="recent-empty"
        >
          <ClipboardList size={28} className="text-[#A1A1AA] mx-auto mb-3" />
          <h3 className="font-heading font-semibold text-lg tracking-tight">
            No recently viewed properties
          </h3>
          <p className="text-[13px] text-[#52525B] mt-1 max-w-md mx-auto">
            Browse listings on the home page — the last 30 properties you open
            will appear here for quick access.
          </p>
          <Link to="/" className="inline-block mt-4">
            <Button size="sm" className="btn-brand rounded-sm">
              Browse listings
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="recent-grid">
          {items.map((item) => (
            <Link
              key={item.id}
              to={`/property/${item.id}`}
              className="bg-white border border-[#E4E4E7] rounded-sm p-4 hover:border-[#0055FF] hover:shadow-md transition-all"
              data-testid={`recent-item-${item.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={13} className="text-[#52525B]" />
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B]">
                  {item.property_type || "Property"}
                </div>
              </div>
              <h3 className="font-heading font-semibold text-[15px] tracking-tight line-clamp-2">
                {item.title || "Untitled listing"}
              </h3>
              {(item.area || item.main_area) && (
                <div className="text-[12px] text-[#71717A] mt-1 inline-flex items-center gap-1">
                  <MapPin size={11} /> {item.area || item.main_area}
                </div>
              )}
              {item.price ? (
                <div className="font-mono-data text-[14px] font-semibold text-[#0055FF] mt-2">
                  {formatINR(item.price)}
                </div>
              ) : null}
            </Link>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
